// @ts-strict-ignore
import React, { useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { Form } from 'react-aria-components';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { FormError } from '@actual-app/components/form-error';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { Paragraph } from '@actual-app/components/paragraph';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';
import type { AccountEntity } from '@actual-app/core/types/models';
import type { TransObjectLiteral } from '@actual-app/core/types/util';

import { useCloseAccountMutation } from '#accounts';
import { AccountAutocomplete } from '#components/autocomplete/AccountAutocomplete';
import { CategoryAutocomplete } from '#components/autocomplete/CategoryAutocomplete';
import { Link } from '#components/common/Link';
import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';
import { useModalEnHoja } from '#components/common/modalesEnHoja';
import { CampoSelectorHoja, TextoHoja } from '#components/common/ModalHoja';
import {
  ConfirmarHoja,
  ListaDestinoHoja,
} from '#components/mobile/ui/ConfirmarHoja';
import { color } from '#components/mobile/ui/tokens';
import { useAccounts } from '#hooks/useAccounts';
import { useCategories } from '#hooks/useCategories';
import { pushModal } from '#modals/modalsSlice';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import { opcionesCuentaDestino } from './destinos';

function needsCategory(
  account: AccountEntity,
  currentTransfer: string,
  accounts: AccountEntity[],
) {
  const acct = accounts.find(a => a.id === currentTransfer);
  const isOffBudget = acct && acct.offbudget === 1;

  // The user must select a category if transferring from a budgeted
  // account to an off budget account
  return account.offbudget === 0 && isOffBudget;
}

type CloseAccountModalProps = Extract<
  ModalType,
  { name: 'close-account' }
>['options'];

export function CloseAccountModal({
  account,
  balance,
  canDelete,
}: CloseAccountModalProps) {
  const { t } = useTranslation(); // Initialize translation hook
  const { data: allAccounts = [] } = useAccounts();
  const accounts = allAccounts.filter(a => a.closed === 0);
  const {
    data: { grouped: categoryGroups, list: categories } = {
      grouped: [],
      list: [],
    },
  } = useCategories();
  const [loading, setLoading] = useState(false);
  const [transferAccountId, setTransferAccountId] = useState('');
  const transferAccount = accounts.find(a => a.id === transferAccountId);
  const [categoryId, setCategoryId] = useState('');
  const category = categories.find(c => c.id === categoryId);

  const [transferError, setTransferError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  const dispatch = useDispatch();
  const { isNarrowWidth } = useResponsive();
  const enHoja = useModalEnHoja('close-account');

  const onSelectAccount = accId => {
    setTransferAccountId(accId);
    if (transferError && accId) {
      setTransferError(false);
    }
  };

  const onSelectCategory = catId => {
    setCategoryId(catId);
    if (categoryError && catId) {
      setCategoryError(false);
    }
  };

  const elegirCuenta = () =>
    dispatch(
      pushModal({
        modal: {
          name: 'account-autocomplete',
          options: {
            includeClosedAccounts: false,
            hiddenAccounts: [account.id],
            onSelect: onSelectAccount,
          },
        },
      }),
    );

  const elegirCategoria = () =>
    dispatch(
      pushModal({
        modal: {
          name: 'category-autocomplete',
          options: {
            categoryGroups,
            onSelect: onSelectCategory,
          },
        },
      }),
    );

  const narrowStyle: CSSProperties = isNarrowWidth
    ? {
        userSelect: 'none',
        height: styles.mobileMinHeight,
        ...styles.mediumText,
      }
    : {};

  const closeAccount = useCloseAccountMutation();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const transferError = balance !== 0 && !transferAccountId;
    setTransferError(transferError);

    const categoryError =
      needsCategory(account, transferAccountId, accounts) && !categoryId;
    setCategoryError(categoryError);

    if (transferError || categoryError) {
      return false;
    }

    setLoading(true);

    closeAccount.mutate({
      id: account.id,
      transferAccountId: transferAccountId || null,
      categoryId: categoryId || null,
    });

    return true;
  };

  return (
    <Modal
      name="close-account"
      isLoading={loading}
      containerProps={{ style: { width: '30vw' } }}
    >
      {({ state }) =>
        enHoja && confirmandoBorrado ? (
          <ConfirmarHoja
            icono="trash"
            pregunta={t('Delete {{accountName}} completely?', {
              accountName: account.name,
            })}
            detalle={t(
              "Its transactions will be deleted too. This can't be undone.",
            )}
            textoAccion={t('Delete completely')}
            testIdAccion="borrar-del-todo-confirmar"
            onConfirmar={() => {
              setLoading(true);
              closeAccount.mutate({ id: account.id, forced: true });
              state.close();
            }}
            onCancelar={() => setConfirmandoBorrado(false)}
            onCerrar={() => state.close()}
          />
        ) : enHoja ? (
          // B6 · H55: hoja de Rumbo en un solo paso, con la lista de cuentas
          // de destino dentro (scroll propio, no se corta).
          <Form
            onSubmit={e => {
              if (onSubmit(e)) {
                state.close();
              }
            }}
          >
            <ConfirmarHoja
              icono={canDelete ? 'trash' : 'wallet'}
              pregunta={t('Close {{accountName}}?', {
                accountName: account.name,
              })}
              detalle={
                canDelete
                  ? t('It has no transactions, so it will be deleted.')
                  : balance !== 0
                    ? t(
                        'Its transactions are kept. Choose where its balance goes.',
                      )
                    : t(
                        'Its transactions are kept and it stops showing in your lists.',
                      )
              }
              contexto={balance !== 0 ? { importe: balance } : null}
              textoAccion={t('Close Account')}
              tipoAccion="submit"
              testIdAccion="cerrar-cuenta"
              secundaria={
                canDelete
                  ? undefined
                  : {
                      texto: t('Delete it completely'),
                      testId: 'cerrar-borrar-del-todo',
                      // Segundo paso: no se puede deshacer.
                      onPress: () => setConfirmandoBorrado(true),
                    }
              }
              onCancelar={() => state.close()}
              onCerrar={() => state.close()}
            >
              {balance !== 0 && (
                <ListaDestinoHoja
                  etiqueta={t('Move the balance to')}
                  opciones={opcionesCuentaDestino(
                    accounts,
                    account.id,
                    t('Off budget'),
                  )}
                  elegida={transferAccountId}
                  onElegir={onSelectAccount}
                  error={transferError ? t('Transfer is required') : undefined}
                  testId="cerrar-cuenta-destino"
                />
              )}
              {balance !== 0 &&
                needsCategory(account, transferAccountId, accounts) && (
                  <View style={{ marginTop: 14 }}>
                    <TextoHoja>
                      <Trans>
                        It's going to an off-budget account, so choose the
                        category for this money:
                      </Trans>
                    </TextoHoja>
                    <CampoSelectorHoja
                      valor={category?.name}
                      placeholder={t('Select category...')}
                      error={categoryError}
                      testId="cerrar-categoria"
                      onPress={elegirCategoria}
                    />
                    {categoryError && (
                      <FormError style={{ marginTop: -8, marginBottom: 6 }}>
                        <Trans>Category is required</Trans>
                      </FormError>
                    )}
                  </View>
                )}
              {!canDelete && (
                <Text
                  style={{
                    display: 'block',
                    marginTop: 12,
                    fontSize: 13,
                    lineHeight: 1.4,
                    color: color.fg3,
                  }}
                >
                  <Trans>
                    "Delete it completely" also deletes all its transactions.
                    Your budget may change.
                  </Trans>
                </Text>
              )}
            </ConfirmarHoja>
          </Form>
        ) : (
          <>
            <ModalHeader
              title={t('Close Account')}
              rightContent={<ModalCloseButton onPress={() => state.close()} />}
            />
            <View>
              <Paragraph>
                <Trans>
                  Are you sure you want to close{' '}
                  <strong>
                    {{ accountName: account.name } as TransObjectLiteral}
                  </strong>
                  ?{' '}
                </Trans>
                {canDelete ? (
                  <span>
                    <Trans>
                      This account has no transactions so it will be permanently
                      deleted.
                    </Trans>
                  </span>
                ) : (
                  <span>
                    <Trans>
                      This account has transactions so we can't permanently
                      delete it.
                    </Trans>
                  </span>
                )}
              </Paragraph>
              <Form
                onSubmit={e => {
                  if (onSubmit(e)) {
                    state.close();
                  }
                }}
              >
                {balance !== 0 && (
                  <View>
                    <Paragraph>
                      <Trans>
                        This account has a balance of{' '}
                        <strong>
                          {
                            {
                              balance: integerToCurrency(balance),
                            } as TransObjectLiteral
                          }
                        </strong>
                        . To close this account, select a different account to
                        transfer this balance to:
                      </Trans>
                    </Paragraph>

                    <View style={{ marginBottom: 15 }}>
                      <AccountAutocomplete
                        includeClosedAccounts={false}
                        hiddenAccounts={[account.id]}
                        value={transferAccountId}
                        inputProps={{
                          placeholder: t('Select account...'),
                          autoFocus: true,
                          ...(isNarrowWidth && {
                            value: transferAccount?.name || '',
                            style: {
                              ...narrowStyle,
                            },
                            onClick: elegirCuenta,
                          }),
                        }}
                        onSelect={onSelectAccount}
                      />
                    </View>

                    {transferError && (
                      <FormError style={{ marginBottom: 15 }}>
                        <Trans>Transfer is required</Trans>
                      </FormError>
                    )}

                    {needsCategory(account, transferAccountId, accounts) && (
                      <View style={{ marginBottom: 15 }}>
                        <Paragraph>
                          <Trans>
                            Since you are transferring the balance from an on
                            budget account to an off budget account, this
                            transaction must be categorized. Select a category:
                          </Trans>
                        </Paragraph>

                        <CategoryAutocomplete
                          categoryGroups={categoryGroups}
                          value={categoryId}
                          inputProps={{
                            placeholder: t('Select category...'),
                            ...(isNarrowWidth && {
                              value: category?.name || '',
                              style: {
                                ...narrowStyle,
                              },
                              onClick: elegirCategoria,
                            }),
                          }}
                          onSelect={onSelectCategory}
                        />

                        {categoryError && (
                          <FormError>
                            <Trans>Category is required</Trans>
                          </FormError>
                        )}
                      </View>
                    )}
                  </View>
                )}

                {!canDelete && (
                  <View style={{ marginBottom: 15 }}>
                    <Text style={{ fontSize: 12 }}>
                      <Trans>
                        You can also{' '}
                        <Link
                          variant="text"
                          onClick={() => {
                            setLoading(true);
                            closeAccount.mutate({
                              id: account.id,
                              forced: true,
                            });
                            state.close();
                          }}
                          style={{ color: theme.errorText }}
                        >
                          force close
                        </Link>{' '}
                        the account which will delete it and all its
                        transactions permanently. Doing so may change your
                        budget unexpectedly since money in it may vanish.
                      </Trans>
                    </Text>
                  </View>
                )}

                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    style={{
                      marginRight: 10,
                      height: isNarrowWidth
                        ? styles.mobileMinHeight
                        : undefined,
                    }}
                    onPress={() => state.close()}
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    style={{
                      height: isNarrowWidth
                        ? styles.mobileMinHeight
                        : undefined,
                    }}
                  >
                    <Trans>Close Account</Trans>
                  </Button>
                </View>
              </Form>
            </View>
          </>
        )
      }
    </Modal>
  );
}
